import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import LandingFooter from "../_components/Footer/LandingFooter";
import { useSettings } from "../_providers/SettingsProvider";

const PricingPage: React.FC = () => {
  const { login, register, isAuthenticated } = useKindeAuth();
  const navigate = useNavigate();
  const siteConfig = useSettings();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Navigation Header */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                <svg
                  className="w-6 h-6 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800">
                InviteWith<span className="text-rose-500">Love</span>
              </h1>
            </Link>
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Link 
                  to="/app" 
                  className="bg-gray-900 text-white px-6 py-2 rounded-full font-medium hover:bg-gray-800 transition-all"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <button 
                    onClick={() => login()}
                    className="text-gray-700 hover:text-rose-600 font-medium transition-colors"
                  >
                    Login
                  </button>
                  <button 
                    onClick={() => navigate('/design')}
                    className="bg-gray-900 text-white px-6 py-2 rounded-full font-medium hover:bg-gray-800 transition-all"
                  >
                    Start Designing
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex-1">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Start for free, upgrade only when you need to invite more guests
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 mb-12">
            
            {/* Free Plan */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Free</h2>
                <p className="text-gray-600 mb-6">Perfect for getting started</p>
                
                <div className="mb-8">
                  <span className="text-5xl font-bold text-gray-900">$0</span>
                </div>

                <ul className="space-y-4">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Up to <strong>15 invited guests</strong></span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Beautiful invitation designs</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">RSVP management</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Guest information tracking</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Mobile-friendly invitations</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Paid Plan */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Unlimited Event</h2>
                <p className="text-gray-600 mb-6">For your special celebration</p>
                
                <div className="mb-8">
                  {siteConfig.isLoading ? (
                    <div className="animate-pulse">
                      <div className="h-12 bg-gray-200 rounded w-32 mb-2"></div>
                    </div>
                  ) : (
                    <>
                      <span className="text-5xl font-bold text-gray-900">{siteConfig.price || '$51'}</span>
                      <span className="text-gray-600 ml-2">USD one-time</span>
                    </>
                  )}
                </div>

                <ul className="space-y-4">
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700"><strong>Unlimited invited guests</strong></span>
                  </li>
                  <li className="flex items-start">
                    <svg className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">All free features included</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Call to Action Section */}
          <div className="max-w-2xl mx-auto text-center mb-20">
            {isAuthenticated ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/app"
                  className="bg-gray-200 text-gray-700 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-300 transition-all"
                >
                  Go to Dashboard
                </Link>
                <Link
                  to="/app/billing/upgrade"
                  className="bg-gradient-to-r from-rose-400 to-pink-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                >
                  Upgrade to Unlimited
                </Link>
              </div>
            ) : (
              <button
                onClick={() => navigate('/design')}
                className="bg-gradient-to-r from-rose-400 to-pink-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                Start Designing Now
              </button>
            )}
          </div>

          {/* FAQ Section */}
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  How does the pricing work?
                </h3>
                <p className="text-gray-600">
                  Start with our free plan which allows up to 15 invited guests. When you need to invite more than 15 guests, 
                  simply upgrade to our paid plan with a one-time payment per event.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Can I create multiple events?
                </h3>
                <p className="text-gray-600">
                  Yes! You can create as many events as you need. Each event starts with the free plan (15 guests), 
                  and you can upgrade individual events as needed.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Is there a refund policy?
                </h3>
                <p className="text-gray-600">
                  Yes, we offer refunds within 14 days of purchase, provided no invitations have been created using paid features. 
                  Once invitations are created using paid features, the purchase is considered used and is non-refundable.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Do prices include tax?
                </h3>
                <p className="text-gray-600">
                  Yes, all prices include GST, VAT, or sales tax where applicable. The price you see is the price you pay.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      <LandingFooter />
    </div>
  );
};

export default PricingPage;